import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLoginPreview from './AppLoginPreview';
import ConsultancyStepCard from './ConsultancyStepCard';
import { AndroidStoreIcon, AppleStoreIcon, PlayTutorialIcon } from './landpageStepIcons';
import OutlineLightButton from './OutlineLightButton';
import SectionTitle from './SectionTitle';
import StepActions from './StepActions';
import styles from './consultancyStep.module.css';
import { TermsModal } from '../terms';
import {
  LANDPAGE_APP_LINKS,
  LANDPAGE_APP_STEP_BUTTONS,
  LANDPAGE_COMMUNITY_LINKS,
  LANDPAGE_CONSULTANCY_STEPS,
  LANDPAGE_HERO,
  LANDPAGE_PROTOCOL_LINKS,
  LANDPAGE_SECTION_TITLE,
  type LandpageConsultancyStepConfig,
} from '../../landpage.constants';
import { appendStudentTokenToPath, STUDENT_TOKEN_QUERY_PARAM } from '../../../../utils/studentPublicLinks';
import { getOnboardingRoutePaths } from '../../../../config/hostApps';

const openInNewTab = (path: string) => {
  window.open(path, '_blank', 'noopener,noreferrer');
};

const formatStepDescription = (description: string) =>
  description.split('\n\n').map((paragraph) => (
    <p key={paragraph} className={styles.descriptionParagraph}>
      {paragraph}
    </p>
  ));

const ConsultancyStepSection: React.FC = () => {
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const studentToken = searchParams.get(STUDENT_TOKEN_QUERY_PARAM)?.trim() || '';
  const onboardingPaths = getOnboardingRoutePaths();

  const buildPath = (path: string) => appendStudentTokenToPath(path, studentToken);

  const renderStepActions = (step: LandpageConsultancyStepConfig) => {
    if (step.id === '01') {
      return (
        <>
          <OutlineLightButton onClick={() => openInNewTab(buildPath(onboardingPaths.anamnese))}>
            {step.primaryButtonLabel}
          </OutlineLightButton>
          <OutlineLightButton onClick={() => openInNewTab(buildPath(onboardingPaths.parq))}>
            {step.secondaryButtonLabel}
          </OutlineLightButton>
        </>
      );
    }

    if (step.id === '03') {
      return (
        <>
          <OutlineLightButton
            compact
            icon={<AppleStoreIcon />}
            href={LANDPAGE_APP_LINKS.iosUrl}
          >
            {LANDPAGE_APP_STEP_BUTTONS.apple}
          </OutlineLightButton>
          <OutlineLightButton
            compact
            icon={<AndroidStoreIcon />}
            href={LANDPAGE_APP_LINKS.androidUrl}
          >
            {LANDPAGE_APP_STEP_BUTTONS.android}
          </OutlineLightButton>
          <OutlineLightButton
            compact
            icon={<PlayTutorialIcon />}
            href={LANDPAGE_APP_LINKS.tutorialUrl}
          >
            {LANDPAGE_APP_STEP_BUTTONS.tutorial}
          </OutlineLightButton>
        </>
      );
    }

    if (step.id === '04') {
      return (
        <>
          <OutlineLightButton
            medium
            href={LANDPAGE_PROTOCOL_LINKS.protocolsUrl}
          >
            {step.primaryButtonLabel}
          </OutlineLightButton>
          <OutlineLightButton
            medium
            href={LANDPAGE_PROTOCOL_LINKS.sosUrl}
          >
            {step.secondaryButtonLabel}
          </OutlineLightButton>
        </>
      );
    }

    if (step.id === '05') {
      return (
        <>
          <OutlineLightButton
            medium
            href={LANDPAGE_COMMUNITY_LINKS.communityUrl}
          >
            {step.primaryButtonLabel}
          </OutlineLightButton>
          <OutlineLightButton
            medium
            href={LANDPAGE_COMMUNITY_LINKS.podcastsUrl}
          >
            {step.secondaryButtonLabel}
          </OutlineLightButton>
        </>
      );
    }

    if (step.id === '06') {
      return (
        <OutlineLightButton
          medium
          href={LANDPAGE_HERO.supportWhatsAppUrl}
        >
          {step.primaryButtonLabel}
        </OutlineLightButton>
      );
    }

    if (step.id === '07') {
      return (
        <OutlineLightButton
          medium
          onClick={() => setIsTermsModalOpen(true)}
        >
          {step.primaryButtonLabel}
        </OutlineLightButton>
      );
    }

    return null;
  };

  const renderStepCard = (step: LandpageConsultancyStepConfig) => {
    if (step.id === '02') {
      return (
        <ConsultancyStepCard
          key={step.id}
          step={step.stepLabel}
          title={step.stepTitle}
          description={step.stepDescription}
          imageSrc={step.imageSrc}
          imageAlt={step.imageAlt}
          imagePosition={step.imagePosition}
        >
          <StepActions>
            <OutlineLightButton onClick={() => openInNewTab(buildPath(onboardingPaths.enviarFotos))}>
              {step.primaryButtonLabel}
            </OutlineLightButton>
          </StepActions>
          {step.secondaryDescription && (
            <p className={styles.followUpDescription}>{step.secondaryDescription}</p>
          )}
          <StepActions>
            <OutlineLightButton onClick={() => openInNewTab(buildPath(onboardingPaths.equipamentos))}>
              {step.secondaryButtonLabel}
            </OutlineLightButton>
          </StepActions>
        </ConsultancyStepCard>
      );
    }

    if (step.id === '03') {
      return (
        <ConsultancyStepCard
          key={step.id}
          step={step.stepLabel}
          title={step.stepTitle}
          description={formatStepDescription(step.stepDescription)}
          imageSrc={step.imageSrc}
          imageAlt={step.imageAlt}
          imagePosition={step.imagePosition}
          titleClassName={styles.stepTitleCompact}
          descriptionClassName={styles.stepDescriptionApp}
          actionsClassName={styles.stepActionsCompact}
          actions={renderStepActions(step)}
        >
          <AppLoginPreview />
        </ConsultancyStepCard>
      );
    }

    if (step.id === '04' || step.id === '06') {
      return (
        <ConsultancyStepCard
          key={step.id}
          step={step.stepLabel}
          title={step.stepTitle}
          description={step.stepDescription}
          imageSrc={step.imageSrc}
          imageAlt={step.imageAlt}
          imagePosition={step.imagePosition}
          descriptionClassName={styles.stepDescriptionApp}
          actions={renderStepActions(step)}
        />
      );
    }

    if (step.id === '05' || step.id === '07') {
      return (
        <ConsultancyStepCard
          key={step.id}
          step={step.stepLabel}
          title={step.stepTitle}
          description={formatStepDescription(step.stepDescription)}
          imageSrc={step.imageSrc}
          imageAlt={step.imageAlt}
          imagePosition={step.imagePosition}
          descriptionClassName={styles.stepDescriptionApp}
          actions={renderStepActions(step)}
        />
      );
    }

    return (
      <ConsultancyStepCard
        key={step.id}
        step={step.stepLabel}
        title={step.stepTitle}
        description={step.stepDescription}
        imageSrc={step.imageSrc}
        imageAlt={step.imageAlt}
        imagePosition={step.imagePosition}
        actions={renderStepActions(step)}
      />
    );
  };

  return (
    <section className={styles.section}>
      <SectionTitle>{LANDPAGE_SECTION_TITLE}</SectionTitle>

      <div className={styles.stepsStack}>
        {LANDPAGE_CONSULTANCY_STEPS.map((step) => renderStepCard(step))}
      </div>

      <TermsModal
        open={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </section>
  );
};

export default ConsultancyStepSection;
